import React from "react"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  normalizeResourceCopyTargets,
  ResourceMenuItems,
  Tooltip,
  useCopyToClipboardFeedback,
  type ResourceMenuAction,
} from "@coval/ui"
import { History, MoreVertical, Stamp, Trash2 } from "lucide-react"

import { useExclusiveActions } from "@/hooks/useExclusiveActions"
import { formatFullTimestamp } from "@/utils/formatting/format-timestamp"

interface ResourceSaveAction {
  onSave: () => void
  pending: boolean
  /** Extra disable conditions beyond the exclusive group (invalid form, no changes). */
  disabled?: boolean
  /** Defaults to "Save"; pass e.g. "Create Metric" in create mode. */
  label?: string
  pendingLabel?: string
  /** Shown on hover while the button is disabled (e.g. a validation error). */
  disabledTooltip?: React.ReactNode
  "data-testid"?: string
}

interface ResourceDuplicateVariant {
  label: string
  onSelect: () => void
  "data-testid"?: string
}

interface ResourceDuplicateAction {
  /** Single-shot duplicate. Mutually exclusive with `variants`. */
  onDuplicate?: () => void
  /** Multiple duplicate flavors rendered as adjacent rows (e.g. with/without associations). */
  variants?: ResourceDuplicateVariant[]
  pending: boolean
  disabled?: boolean
  label?: string
  "data-testid"?: string
}

interface ResourceDeleteAction {
  onDelete: () => void
  pending: boolean
  disabled?: boolean
  label?: string
  "data-testid"?: string
}

interface ResourceVersionHistoryAction {
  onOpen: () => void
  label?: string
  "data-testid"?: string
}

export type ResourceMenuEntry = Omit<ResourceMenuAction, "destructive">

export interface ResourceAttribution {
  createdAt?: string | null
  createdBy?: string | null
  updatedAt?: string | null
  updatedBy?: string | null
}

interface ResourceHeaderActionsProps {
  save?: ResourceSaveAction
  duplicate?: ResourceDuplicateAction
  delete?: ResourceDeleteAction
  versionHistory?: ResourceVersionHistoryAction
  /** Resource-specific rows rendered before the shared Copy ID and Copy URL rows. */
  additionalActions?: ResourceMenuEntry[]
  /** Copied by the shared Copy ID row when available. */
  resourceId?: string | null
  /** Copied by the shared Copy URL row when available. */
  resourceUrl?: string | null
  /** Raw resource metadata rendered in the shared read-only attribution footer when available. */
  attribution?: ResourceAttribution | null
  /** Visible controls rendered between the dot menu and Save (e.g. Cancel). */
  trailing?: React.ReactNode
  /** aria-label and tooltip for the dot-menu trigger. Defaults to "More actions". */
  menuLabel?: string
  /** Test identifier for the dot-menu trigger. */
  menuTestId?: string
  "data-testid"?: string
}

/**
 * The standard action cluster for a resource detail header ("meta bar"):
 * a `⋯` dot menu holding resource-specific actions, the shared Copy ID and
 * Copy URL rows, Delete, and best-effort attribution — followed by any visible
 * trailing controls and the primary Save button.
 *
 * Save/Duplicate/Delete form one exclusive group: while any is in flight the
 * others disable and the in-flight one shows a spinner (on the Save button, or
 * on the dot-menu trigger and its row for menu actions). Read-only entries
 * like Version history stay interactive.
 */
export function ResourceHeaderActions({
  save,
  duplicate,
  delete: deleteAction,
  versionHistory,
  additionalActions = [],
  resourceId,
  resourceUrl,
  attribution,
  trailing,
  menuLabel = "More actions",
  menuTestId = "resource-actions-menu",
  "data-testid": dataTestId,
}: ResourceHeaderActionsProps) {
  const { copied, copy } = useCopyToClipboardFeedback()
  const actions = useExclusiveActions({
    save: save?.pending ?? false,
    duplicate: duplicate?.pending ?? false,
    delete: deleteAction?.pending ?? false,
  })

  const menuActions: ResourceMenuAction[] = []

  if (versionHistory) {
    menuActions.push({
      label: versionHistory.label ?? "Version history",
      onSelect: versionHistory.onOpen,
      icon: <History />,
      "data-testid": versionHistory["data-testid"],
    })
  }

  menuActions.push(...additionalActions)

  if (duplicate) {
    const duplicateProps = actions.props("duplicate")
    const shared = {
      icon: <Stamp />,
      disabled: duplicateProps.disabled || duplicate.disabled,
      loading: duplicateProps.loading,
    }
    if (duplicate.variants) {
      menuActions.push(
        ...duplicate.variants.map((variant) => ({ ...variant, ...shared })),
      )
    } else if (duplicate.onDuplicate) {
      menuActions.push({
        label: duplicate.label ?? "Duplicate",
        onSelect: duplicate.onDuplicate,
        "data-testid": duplicate["data-testid"],
        ...shared,
      })
    }
  }

  const copyTargets = normalizeResourceCopyTargets({
    id: resourceId,
    url: resourceUrl,
  })

  const deleteProps = deleteAction ? actions.props("delete") : null
  const deleteRow: ResourceMenuAction | null =
    deleteAction && deleteProps
      ? {
          label: deleteAction.label ?? "Delete",
          onSelect: deleteAction.onDelete,
          icon: <Trash2 />,
          disabled: deleteProps.disabled || deleteAction.disabled,
          loading: deleteProps.loading,
          destructive: true,
          "data-testid": deleteAction["data-testid"],
        }
      : null
  if (deleteRow) {
    menuActions.push(deleteRow)
  }

  const menuBusy =
    (duplicate?.pending ?? false) || (deleteAction?.pending ?? false)
  const attributionEntries = getAttributionEntries(attribution)
  const attributionFooter =
    attributionEntries.length > 0 ? (
      <ResourceAttributionFooter entries={attributionEntries} />
    ) : null
  const hasRows =
    menuActions.length > 0 ||
    Boolean(copyTargets.id) ||
    Boolean(copyTargets.url)
  const hasMenu = hasRows || attributionFooter != null

  return (
    <div className="flex items-center gap-2" data-testid={dataTestId}>
      {hasMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              icon
              tooltip={copied ? "Copied!" : menuLabel}
              tooltipProps={{ side: "bottom", forceOpen: copied }}
              loading={menuBusy}
              aria-label={menuLabel}
              data-testid={menuTestId}
            >
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <ResourceMenuItems
              actions={menuActions}
              copy={copyTargets}
              onCopy={(value) => {
                void copy(value)
              }}
              item={DropdownMenuItem}
              separator={DropdownMenuSeparator}
            />
            {attributionFooter != null && hasRows && <DropdownMenuSeparator />}
            {attributionFooter != null && (
              <div className="px-2 py-1.5 text-xs text-coval-text-muted">
                {attributionFooter}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {trailing}
      {save && (
        <SaveButton
          save={save}
          loading={actions.props("save").loading}
          groupDisabled={actions.props("save").disabled}
        />
      )}
    </div>
  )
}

type ResourceAttributionEntry = readonly [label: string, value: string]

function getAttributionEntries(
  attribution: ResourceAttribution | null | undefined,
): ResourceAttributionEntry[] {
  if (!attribution) {
    return []
  }

  const entries: Array<
    readonly [label: string, value: string | null | undefined]
  > = [
    ["Created At", formatFullTimestamp(attribution.createdAt)],
    ["Created By", attribution.createdBy],
    ["Updated At", formatFullTimestamp(attribution.updatedAt)],
    ["Updated By", attribution.updatedBy],
  ]

  return entries.filter((entry): entry is ResourceAttributionEntry =>
    Boolean(entry[1]),
  )
}

function ResourceAttributionFooter({
  entries,
}: {
  entries: ResourceAttributionEntry[]
}) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
      {entries.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd className="text-coval-text">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

function SaveButton({
  save,
  loading,
  groupDisabled,
}: {
  save: ResourceSaveAction
  loading: boolean
  groupDisabled: boolean
}) {
  const button = (
    <Button
      variant="default"
      onClick={save.onSave}
      loading={loading}
      disabled={groupDisabled || save.disabled}
      className={
        save.disabled && save.disabledTooltip
          ? "pointer-events-none"
          : undefined
      }
      aria-label={save.label ?? "Save"}
      data-testid={save["data-testid"]}
    >
      {loading
        ? (save.pendingLabel ?? save.label ?? "Save")
        : (save.label ?? "Save")}
    </Button>
  )
  if (save.disabled && save.disabledTooltip) {
    return (
      <Tooltip content={save.disabledTooltip} side="bottom">
        <span tabIndex={0} className="inline-flex">
          {button}
        </span>
      </Tooltip>
    )
  }
  return button
}
